import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import BuyCreditsModal from "@/components/UpgradeModal";
import CreditsBar from "@/components/CreditsBar";
import LowBalanceBanner from "@/components/LowBalanceBanner";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LANGUAGES, SOURCE_LANGUAGES, TONES } from "@/data/languages";
import { motion } from "framer-motion";
import { ArrowRightLeft, Copy, Loader2, Wand2, FileDown, FileText } from "lucide-react";

export default function Translate() {
  const { user, refresh } = useAuth();
  const [source, setSource] = useState("auto");
  const [target, setTarget] = useState("English");
  const [tone, setTone] = useState("natural");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const [buyReason, setBuyReason] = useState("");
  const [exporting, setExporting] = useState("");

  const remaining = user?.remaining ?? 0;
  const unlimited = user?.unlimited;
  const inputChars = input.length;
  const notEnough = !unlimited && inputChars > remaining;

  const swap = () => {
    if (source === "auto") return;
    setSource(target);
    setTarget(source);
    setInput(output);
    setOutput(input);
  };

  const openBuy = (reason) => {
    setBuyReason(reason || "");
    setShowBuy(true);
  };

  const translate = async () => {
    if (!input.trim()) return;
    if (!unlimited && remaining <= 0) return openBuy("Hai esaurito le battute. Abbonati o acquista un pacchetto per continuare.");
    if (notEnough) return openBuy(`Questo testo richiede ${inputChars} battute ma ne hai ${remaining}. Abbonati o acquista un pacchetto.`);
    setLoading(true);
    setOutput("");
    try {
      const { data } = await api.post("/translate", { text: input, source_lang: source, target_lang: target, tone });
      setOutput(data.translated_text);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        openBuy(apiErr(detail));
      } else {
        toast.error(apiErr(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const copyOut = () => {
    navigator.clipboard.writeText(output);
    toast.success("Traduzione copiata");
  };

  const exportOut = async (format) => {
    if (!output) return;
    setExporting(format);
    try {
      const res = await api.post("/translate/export", { text: output, format }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "docx" ? "traduzione.docx" : "traduzione.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Errore durante l'esportazione");
    } finally {
      setExporting("");
    }
  };

  const langItem = (l) => (
    <SelectItem key={l.code} value={l.code} data-testid={`lang-opt-${l.code}`}>
      <span className="mr-2">{l.flag}</span>{l.label}
    </SelectItem>
  );

  const renderOutput = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Traduzione in corso...
        </div>
      );
    }
    if (output) {
      return <span data-testid="output-text">{output}</span>;
    }
    return <span className="text-muted-foreground/60">La traduzione apparirà qui.</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mb-6 items-end">
          <div>
            <h1 className="font-heading text-3xl font-light tracking-tight">Traduzione testo</h1>
            <p className="text-sm text-muted-foreground mt-1">Qualità madrelingua, istantanea. Consumi battute dal tuo saldo.</p>
          </div>
          <CreditsBar user={user} />
        </div>

        <LowBalanceBanner user={user} onBuy={() => openBuy("")} />

        <div className="flex justify-end mb-4">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-mono-label uppercase tracking-wider text-muted-foreground">Tono di voce</span>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="tone-select" className="w-[220px] rounded-full font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONES.map((t) => (
                  <SelectItem key={t.code} value={t.code} data-testid={`tone-opt-${t.code}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-border bg-card flex flex-col overflow-hidden focus-within:ring-2 focus-within:ring-brand-blue/40 transition-shadow">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="source-lang" className="w-[180px] border-0 shadow-none focus:ring-0 h-8 px-2 font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{SOURCE_LANGUAGES.map(langItem)}</SelectContent>
              </Select>
              <span className={`text-xs font-mono ${notEnough ? "text-destructive" : "text-muted-foreground"}`}>
                {inputChars} {inputChars === 1 ? "battuta" : "battute"}
              </span>
            </div>
            <Textarea
              data-testid="source-text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi o incolla il testo da tradurre..."
              className="flex-1 min-h-[340px] resize-none border-0 focus-visible:ring-0 p-6 text-base bg-transparent"
            />
          </motion.div>

          <div className="flex lg:flex-col items-center justify-center">
            <button
              data-testid="swap-btn"
              onClick={swap}
              disabled={source === "auto"}
              className="h-11 w-11 rounded-full border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-brand-blue hover:border-brand-blue/50 disabled:opacity-40 transition-colors"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className={`relative rounded-2xl border bg-card flex flex-col overflow-hidden ${loading ? "border-transparent" : "border-border"}`}>
            {loading && <div className="absolute top-0 left-0 right-0 h-0.5 tracing-beam" />}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="target-lang" className="w-[180px] border-0 shadow-none focus:ring-0 h-8 px-2 font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{LANGUAGES.map(langItem)}</SelectContent>
              </Select>
              {output && (
                <div className="flex items-center gap-1">
                  <button data-testid="export-pdf-btn" onClick={() => exportOut("pdf")} disabled={exporting !== ""}
                    title="Scarica PDF"
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:text-brand-pink hover:border-brand-pink/50 transition-colors disabled:opacity-50">
                    {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
                  </button>
                  <button data-testid="export-docx-btn" onClick={() => exportOut("docx")} disabled={exporting !== ""}
                    title="Scarica Word"
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:text-brand-blue hover:border-brand-blue/50 transition-colors disabled:opacity-50">
                    {exporting === "docx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Word
                  </button>
                  <button data-testid="copy-btn" onClick={copyOut} title="Copia"
                    className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex-1 min-h-[340px] p-6 text-base whitespace-pre-wrap overflow-auto">
              {renderOutput()}
            </div>
          </motion.div>
        </div>

        <div className="flex items-center justify-between mt-6 flex-wrap gap-4">
          <p className="text-xs text-muted-foreground">
            Ogni traduzione consuma battute in base alla lunghezza del testo di partenza.
          </p>
          <Button
            data-testid="translate-btn"
            onClick={translate}
            disabled={loading || !input.trim()}
            className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11 px-8 gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Traduci
          </Button>
        </div>
      </div>
      <BuyCreditsModal open={showBuy} onOpenChange={setShowBuy} reason={buyReason} />
    </div>
  );
}
